/**
 * FlahaSOIL v2 — Soil physics water summary bar chart (dark-native).
 */
import { Box, Stack, Typography, LinearProgress } from "@mui/material";
import type { ScientificChartData } from "../types/ScientificChartTypes";
import type { SoilPhysicsChartData } from "../adapters/soilPhysicsChartAdapter";
import { chartTheme } from "../utils/chartTheme";

interface SoilPhysicsWaterChartProps {
  chartData: ScientificChartData<SoilPhysicsChartData | null>;
}

export function SoilPhysicsWaterChart({ chartData }: SoilPhysicsWaterChartProps) {
  const { data, accessibilitySummary, units } = chartData;

  if (!data) {
    return (
      <Typography variant="body2" color="text.secondary">
        No physics data available.
      </Typography>
    );
  }

  const items = [
    { label: "Saturation", value: data.saturation, color: chartTheme.saturation },
    { label: "Field Capacity", value: data.fieldCapacity, color: chartTheme.fieldCapacity },
    { label: "Wilting Point", value: data.wiltingPoint, color: chartTheme.wiltingPoint },
    { label: "Plant Available Water", value: data.plantAvailableWater, color: chartTheme.seriesPrimary },
  ];

  const maxVal = Math.max(50, data.saturation);

  return (
    <Stack spacing={2} aria-label={accessibilitySummary}>
      {items.map((item) => (
        <Box key={item.label}>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" fontWeight={600}>{item.label}</Typography>
            <Typography variant="caption" sx={{ color: "workspace.textSecondary" }}>
              {item.value.toFixed(1)} {units.water}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={(item.value / maxVal) * 100}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: chartTheme.barTrack,
              "& .MuiLinearProgress-bar": {
                backgroundColor: item.color,
                boxShadow: `0 0 10px ${item.color}55`,
              },
            }}
          />
        </Box>
      ))}
    </Stack>
  );
}
